import { Apis } from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    payload?.totalCount ??
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return {
    items,
    pagination,
    totalCount,
    category_count: payload?.category_count,
    payload,
  };
};

// --- Interior designs ---
export const useCommercialInteriorDesign = () => {
  const getCommercialInteriorDesign = async (
    setCommercialInteriorDesignData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = "",
  ) => {
    try {
      const params = {};
      if (searchFilter) {
        params.searchFilter = searchFilter;
      }
      const response = await Apis.getCommercialInteriorDesign(offset, limit, params);
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      setCommercialInteriorDesignData(items);
      setTotal(totalCount);
      return { data: items, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error fetching commercial interior design:", error);
      throw new Error(getApiError(error, "Failed to fetch commercial interior designs."));
    }
  };

  const createCommercialInteriorDesign = async (data) => {
    try {
      const response = await Apis.createCommercialInteriorDesign(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error creating commercial interior design:", error);
      throw error;
    }
  };

  const updateCommercialInteriorDesign = async (id, data) => {
    try {
      const response = await Apis.updateCommercialInteriorDesign(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating commercial interior design:", error);
      throw error;
    }
  };

  const deleteCommercialInteriorDesign = async (id) => {
    try {
      const response = await Apis.deleteCommercialInteriorDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting commercial interior design:", error);
      throw error;
    }
  };

  const getCommercialInteriorDesignById = async (id, setInteriorData) => {
    try {
      const response = await Apis.getCommercialInteriorDesignById(id);
      const design = unwrapResponseBody(response?.data);
      if (typeof setInteriorData === "function") setInteriorData(design);
      return design;
    } catch (error) {
      console.error("Error fetching commercial interior design by id:", error);
      throw error;
    }
  };

  const showCommercialInteriorDesignOnHomeScreen = async (commercialInteriorDesignId, homeScreen) => {
    try {
      const data = {
        commercialInteriorDesignId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addCommercialInteriorDesignToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateCommercialInteriorDesignPosition = async (commercialInteriorDesignId, newPosition) => {
    try {
      const response = await Apis.updateCommercialInteriorDesignPosition({
        commercialInteriorDesignId,
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating commercial interior design position:", error);
      throw error;
    }
  };

  return {
    getCommercialInteriorDesign,
    createCommercialInteriorDesign,
    updateCommercialInteriorDesign,
    deleteCommercialInteriorDesign,
    getCommercialInteriorDesignById,
    showCommercialInteriorDesignOnHomeScreen,
    updateCommercialInteriorDesignPosition,
  };
};
